import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { FormGroup, FormControl, ControlLabel, Button } from 'react-bootstrap';
import format from 'date-fns/format';
import Modal from 'components/Modal/Modal';
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown';
import styles from './EditJobModal.module.scss';

const EditJobModal = ({ brief, show, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    jobTitle: '',
    description: '',
    location: '',
    locationText: '',
    remote: 0,
    budget: '',
    closingDate: '',
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when brief changes or modal opens
  useEffect(() => {
    if (brief && show) {
      // Format the closing date for the date input (YYYY-MM-DD)
      let closingDateFormatted = '';
      if (brief.expires_at) {
        const date = new Date(brief.expires_at);
        closingDateFormatted = date.toISOString().split('T')[0];
      }

      setFormData({
        jobTitle: brief.text || '',
        description: brief.spec || '',
        location: brief.location || '',
        locationText: brief.location?.text || brief.location || '',
        remote: brief.remote || 0,
        budget: brief.budget && brief.budget > 0 ? brief.budget / 100 : '',
        closingDate: closingDateFormatted,
      });
      setErrors({});
    }
  }, [brief, show]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.locationText.trim() && !formData.remote) {
      newErrors.location = 'Location is required';
    }

    if (!formData.budget || formData.budget === '') {
      newErrors.budget = 'Budget is required';
    }

    if (!formData.closingDate) {
      newErrors.closingDate = 'Job closing date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Format closing date to include time (end of day)
    const closingDateTime = new Date(formData.closingDate);
    closingDateTime.setHours(23, 59, 59, 999);

    // Prepare the data payload for the API
    const payload = {
      text: formData.jobTitle,
      spec: formData.description,
      location_text: formData.locationText,
      remote: formData.remote,
      amount: formData.budget ? parseFloat(formData.budget) * 100 : -1,
      expires_at: format(closingDateTime, 'yyyy-MM-dd HH:mm:ss'),
    };

    onSave(payload);
  };

  const handleCancel = () => {
    setFormData({
      jobTitle: '',
      description: '',
      location: '',
      locationText: '',
      remote: 0,
      budget: '',
      closingDate: '',
    });
    setErrors({});
    onClose();
  };

  const handleLocationChange = (location) => {
    if (!location.city || !location.country) {
      setFormData((prev) => ({
        ...prev,
        remote: 1,
        locationText: '',
      }));
    } else {
      const locationText =
        location.city && location.country
          ? location.city + ', ' + location.country
          : location.country;
      setFormData((prev) => ({
        ...prev,
        remote: 0,
        locationText: locationText,
      }));
    }

    // Clear error for location field
    if (errors.location) {
      setErrors((prev) => ({
        ...prev,
        location: null,
      }));
    }
  };

  const handleLocationRemote = () => {
    setFormData((prev) => ({
      ...prev,
      remote: 1,
      locationText: '',
    }));

    // Clear error for location field
    if (errors.location) {
      setErrors((prev) => ({
        ...prev,
        location: null,
      }));
    }
  };

  if (!brief) {
    return null;
  }

  return (
    <Modal
      show={show}
      onExit={handleCancel}
      closeButton
      modalClassName={styles.modal}
    >
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>Edit Job Details</h2>

        <form onSubmit={handleSubmit} className={styles.form}>
          <FormGroup
            controlId="jobTitle"
            className={errors.jobTitle ? styles.hasError : ''}
          >
            <ControlLabel className={styles.label}>Job Title</ControlLabel>
            <FormControl
              type="text"
              value={formData.jobTitle}
              onChange={(e) => handleChange('jobTitle', e.target.value)}
              placeholder="Senior Frontend Developer"
              className={styles.input}
              disabled={saving}
            />
            {errors.jobTitle && (
              <div className={styles.errorText}>{errors.jobTitle}</div>
            )}
          </FormGroup>

          <FormGroup
            controlId="description"
            className={errors.description ? styles.hasError : ''}
          >
            <ControlLabel className={styles.label}>Description</ControlLabel>
            <FormControl
              componentClass="textarea"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="We are looking for an experienced Frontend Developer to join our team and help build the next generation of our product."
              className={styles.textarea}
              disabled={saving}
              rows={8}
            />
            {errors.description && (
              <div className={styles.errorText}>{errors.description}</div>
            )}
          </FormGroup>

          <FormGroup
            controlId="location"
            className={errors.location ? styles.hasError : ''}
          >
            <ControlLabel className={styles.label}>
              Will your freelancer be required onsite?
            </ControlLabel>
            <SingleLocationDropdown
              defaultLocation={formData.location}
              onLocationChange={handleLocationChange}
              onLocationReset={handleLocationRemote}
              placeholder="Enter location or leave empty for remote"
              showResetIcon={true}
              shouldClearOnBlur={true}
              shouldResetOnBlur={true}
              disabled={saving}
            />
            {errors.location && (
              <div className={styles.errorText}>{errors.location}</div>
            )}
          </FormGroup>

          <FormGroup
            controlId="budget"
            className={errors.budget ? styles.hasError : ''}
          >
            <ControlLabel className={styles.label}>Budget</ControlLabel>
            <div className={styles.budgetInputWrapper}>
              <FormControl
                type="number"
                value={formData.budget}
                onChange={(e) => handleChange('budget', e.target.value)}
                placeholder="120000"
                className={`${styles.input} ${styles.budgetInput}`}
                disabled={saving}
                min="0"
                step="0.01"
              />
              <span className={styles.currencySymbol}>
                {brief.currency === 'usd'
                  ? '$'
                  : brief.currency === 'gbp'
                  ? '£'
                  : '€'}
              </span>
            </div>
            {errors.budget && (
              <div className={styles.errorText}>{errors.budget}</div>
            )}
          </FormGroup>

          <FormGroup
            controlId="closingDate"
            className={errors.closingDate ? styles.hasError : ''}
          >
            <ControlLabel className={styles.label}>
              Job Closing Date
            </ControlLabel>
            <FormControl
              type="date"
              value={formData.closingDate}
              onChange={(e) => handleChange('closingDate', e.target.value)}
              className={styles.input}
              disabled={saving}
              min={new Date().toISOString().split('T')[0]}
            />
            {errors.closingDate && (
              <div className={styles.errorText}>{errors.closingDate}</div>
            )}
          </FormGroup>

          <div className={styles.buttonContainer}>
            <Button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bsStyle="primary"
              disabled={saving}
              className={styles.saveButton}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

EditJobModal.propTypes = {
  brief: PropTypes.object,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

EditJobModal.defaultProps = {
  saving: false,
};

export default EditJobModal;
